export interface DateOption {
  value: string // ISO date string (YYYY-MM-DD)
  label: string // Display string (e.g., "January 2024")
}

export interface DateOptionsResult {
  futureOptions: DateOption[]
  pastOptions: DateOption[]
}

/**
 * Generate month options with future and past groupings
 * @param futureMonths Number of future months to include (default: 12)
 * @param pastMonths Number of past months to include (default: 12)
 * @returns Object with futureOptions and pastOptions arrays
 */
export const generateMonthOptions = (
  futureMonths: number = 12,
  pastMonths: number = 12
): DateOptionsResult => {
  const currentDate = new Date()
  const currentMonth = currentDate.getMonth()
  const currentYear = currentDate.getFullYear()

  const futureOptions: DateOption[] = []
  const pastOptions: DateOption[] = []

  // Generate future months (current month + next N months)
  for (let i = 0; i < futureMonths; i++) {
    const date = new Date(currentYear, currentMonth + i, 1)
    const isoString = date.toISOString().split('T')[0] // YYYY-MM-DD format
    const displayString = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    })
    futureOptions.push({
      value: isoString,
      label: displayString,
    })
  }

  // Generate past months (previous N months, ordered from most recent to oldest)
  for (let i = 1; i <= pastMonths; i++) {
    const date = new Date(currentYear, currentMonth - i, 1)
    const isoString = date.toISOString().split('T')[0] // YYYY-MM-DD format
    const displayString = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    })
    pastOptions.push({
      value: isoString,
      label: displayString,
    })
  }

  return { futureOptions, pastOptions }
}

/**
 * Get the first day of next month as an ISO date string
 * @returns ISO date string (YYYY-MM-DD) for the first day of next month
 */
export const getNextMonthStartDate = (): string => {
  const currentDate = new Date()
  const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
  return nextMonth.toISOString().split('T')[0]
}
